import React, { useState, useEffect, useContext } from 'react';
import createAuth0Client from '@auth0/auth0-spa-js';
import { analyticsV2 } from 'analytics';
import { getTwoLetterLanguageCode } from 'helpers';
import { useAppContext } from 'context/AppContext';
import { isLandrAppHostname } from 'helpers/isLandrAppHostname';
import { EventId } from 'enum/eventId';
import { AuthenticationContextType } from 'components/Authentication/types';
import { log } from '../../log';

const logger = log.getInstance('AuthenticationProvider');

const makeCollaborationReturnPath = (id: string, type: string, code: string): string => {
    if (type === 'Audio') {
        return `/library/tracks/${id}?collaborationCode=${code}&referrer=authsite`;
    } else if (type === 'Project') {
        return `/library/projects/${id}?collaborationCode=${code}&referrer=authsite`;
    } else {
        return '';
    }
};

const fetchCollaborationReturnPath = (code: string): Promise<string> => {
    return fetch(`${process.env.GATSBY_API_ENDPOINT}/library/api/v1/collaborators/link/${code}`)
        .then((response) => response.json())
        .then((item) => (item ? makeCollaborationReturnPath(item.itemId, item.itemType, code) : ''))
        .catch(() => {
            return '';
        });
};

/**
 *
 * @param redirectUrl
 * @param currentPageParams
 * @param pageLang - Current guest site language
 * @param homePage - The homePage asignation on signup, set for each prismic page
 */
const augmentQueryParams = (
    redirectUrl: URL,
    currentPageParams: URLSearchParams,
    pageLang: string,
    homePage: string | null,
) => {
    const redirectUrlParams = new URLSearchParams(redirectUrl.search);

    if (pageLang) {
        redirectUrlParams.set('locale', getTwoLetterLanguageCode(pageLang));
    }

    if (homePage) {
        redirectUrlParams.set('homePage', homePage);
    }

    // Merge currentPageParams and redirectUrlParams, only if redirectUrlParams doesn't already contain the param
    for (const [key, value] of currentPageParams.entries()) {
        if (!redirectUrlParams.has(key) && key !== 'returnUrl') {
            redirectUrlParams.append(key, value);
        }
    }
    return redirectUrlParams;
};

type MakeRedirectUrlResult = { redirectUrl: URL; redirectUrlParams: URLSearchParams };

const makeRedirectUrl = async (
    currentPageParams: URLSearchParams,
    pageLang: string,
    homePage: string | null,
): Promise<MakeRedirectUrlResult> => {
    const appEndpoint = process.env.GATSBY_APP_ENDPOINT as string;
    const plan = currentPageParams.get('plan');
    const planFrequency = currentPageParams.get('planFrequency');
    const collaborationCode = currentPageParams.get('collaborationCode');
    const returnUrl = currentPageParams.get('returnUrl');
    let redirectUrl: URL | null = null;

    // Determine where to redirect the user after registering & logging in
    if (plan) {
        // User has chosen a billing plan
        redirectUrl = new URL(`${appEndpoint}/payment/subscription/checkout/${plan}/${planFrequency || 'yearly'}`);
    } else if (collaborationCode) {
        // Invitation to collaborate on an Asset/Project
        redirectUrl = new URL(appEndpoint + (await fetchCollaborationReturnPath(collaborationCode)));
    } else if (returnUrl) {
        // Use a try/catch in case we are unable to parse the URLs
        try {
            // User is supposed to be redirected to a URL specified in the returnUrl query param
            const returnUrlObject = new URL(returnUrl);
            // Only allow redirections to LANDR apps to prevent malicious redirections
            if (isLandrAppHostname(returnUrlObject.hostname)) {
                redirectUrl = returnUrlObject;
            } else {
                // Log a warning
                logger.warn(
                    'An unsupported returnUrl was used as a returnUrl query param',
                    EventId.UnsupportedReturnUrlWarning,
                );
            }
        } catch (e) {
            // This error will be caught if we are unable to parse the URL using new URL(...)
            logger.warn(e.message, EventId.CatchedReturnUrlWarning);
        }
    }

    if (!redirectUrl) {
        // Send the user to the web app home page
        redirectUrl = new URL(appEndpoint);
    }

    return {
        redirectUrl,
        redirectUrlParams: augmentQueryParams(redirectUrl, currentPageParams, pageLang, homePage),
    };
};

const auth0Promise =
    typeof window !== 'undefined'
        ? createAuth0Client({
              audience: process.env.GATSBY_AUTH0_AUDIENCE,
              client_id: process.env.GATSBY_AUTH0_CLIENT_ID as string,
              domain: process.env.GATSBY_AUTH0_DOMAIN as string,
              redirect_uri: typeof window !== 'undefined' ? window.location.origin : '',
              responseType: 'token id_token',
              scope: 'openid profile',
          })
        : Promise.resolve(null);

const AuthenticationContext = React.createContext({
    isAuthenticated: false,
    loginUrl: '',
    signupUrl: '',
    logout: () => undefined,
    authorizationToken: undefined,
} as AuthenticationContextType);

const useAuthentication = (): AuthenticationContextType => useContext(AuthenticationContext);

interface AuthenticationProviderProps {
    children: React.ReactNode;
}

const AuthenticationProvider: React.FC = ({ children }: AuthenticationProviderProps) => {
    const href = typeof window !== 'undefined' && window.location.href;
    const searchParams = typeof window !== 'undefined' && window.location.search;
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authorizationToken, setAuthorizationToken] = useState<string>();
    const [loginUrl, setLoginUrl] = useState('');
    const [signupUrl, setSignupUrl] = useState('');
    const appContext = useAppContext();

    useEffect(() => {
        const execute = async () => {
            const auth0 = await auth0Promise;

            // auth0 should always be defined except for Gatsby SSR.
            if (auth0) {
                let token;

                try {
                    token = await auth0.getTokenSilently();
                } catch (error) {
                    setIsAuthenticated(false);
                    setAuthorizationToken(undefined);
                }

                setAuthorizationToken(token);
                setIsAuthenticated(await auth0.isAuthenticated());
            }
        };

        typeof window !== 'undefined' && execute();
    }, []);

    useEffect(() => {
        const makeUrl = async () => {
            if (typeof window !== 'undefined') {
                const currentPageParams = new URLSearchParams(window.location.search);
                const isSamples = typeof window !== 'undefined' && href && href.indexOf('samples') > -1;
                const homePage = isSamples ? 'Samples' : appContext.homePage;

                const { redirectUrl, redirectUrlParams } = await makeRedirectUrl(
                    currentPageParams,
                    appContext.pageContext.lang,
                    homePage,
                );

                let url = `${redirectUrl.origin}${redirectUrl.pathname}`;

                if (redirectUrlParams && !!redirectUrlParams.toString()) {
                    url += `?${redirectUrlParams.toString()}`;
                }

                setLoginUrl(url);
                setSignupUrl(`${url}${url.includes('?') ? '&' : '?'}isSignup=true`);
            }
        };

        makeUrl();
    }, [appContext.pageContext.lang, appContext.homePage, searchParams, href]);

    const logout = async () => {
        analyticsV2.reset();
        const auth0 = await auth0Promise;
        return auth0 && auth0.logout();
    };

    return (
        <AuthenticationContext.Provider
            value={{
                isAuthenticated,
                loginUrl,
                logout,
                signupUrl,
                authorizationToken,
            }}
        >
            <>{children}</>
        </AuthenticationContext.Provider>
    );
};

export { AuthenticationProvider, useAuthentication };
